import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiMapPin, FiExternalLink, FiHeart, FiShare2, FiFilter, FiSearch, FiCalendar } = FiIcons;

const Candidates = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedState, setSelectedState] = useState('all');
  const [supportedCandidates, setSupportedCandidates] = useState(new Set([1, 3, 5]));

  const levels = [
    { id: 'all', name: 'All Levels' },
    { id: 'federal', name: 'Federal' },
    { id: 'state', name: 'State' },
    { id: 'local', name: 'Local' }
  ];

  const states = [
    { id: 'all', name: 'All States' },
    { id: 'TX', name: 'Texas' },
    { id: 'FL', name: 'Florida' },
    { id: 'GA', name: 'Georgia' },
    { id: 'AZ', name: 'Arizona' },
    { id: 'OH', name: 'Ohio' }
  ];

  const candidates = [
    {
      id: 1,
      name: 'Sarah Mitchell',
      position: 'U.S. Senate',
      level: 'federal',
      state: 'TX',
      party: 'Republican',
      photo: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      bio: 'Conservative leader with 15 years of experience fighting for limited government and individual liberty.',
      priorities: ['Border Security', 'Economic Freedom', 'Constitutional Rights'],
      website: 'https://sarahmitchell.com',
      twitter: '@SarahMitchellTX',
      facebook: 'SarahMitchellForSenate',
      endorsements: 45,
      fundraised: '$2.3M',
      nextEvent: 'Town Hall - March 20th'
    },
    {
      id: 2,
      name: 'Robert Chen',
      position: 'Governor',
      level: 'state',
      state: 'FL',
      party: 'Republican',
      photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      bio: 'Successful businessman and veteran committed to bringing conservative values to state government.',
      priorities: ['Education Reform', 'Tax Reduction', 'Public Safety'],
      website: 'https://robertchen.com',
      twitter: '@RobertChenFL',
      facebook: 'RobertChenForGovernor',
      endorsements: 67,
      fundraised: '$4.1M',
      nextEvent: 'Rally - March 18th'
    },
    {
      id: 3,
      name: 'Maria Rodriguez',
      position: 'House District 15',
      level: 'state',
      state: 'AZ',
      party: 'Republican',
      photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      bio: 'Local business owner and mother fighting for family values and fiscal responsibility.',
      priorities: ['School Choice', 'Small Business', 'Family Values'],
      website: 'https://mariarodriguez.com',
      twitter: '@MariaRodriguezAZ',
      facebook: 'MariaForArizona',
      endorsements: 28,
      fundraised: '$450K',
      nextEvent: 'Community Forum - March 22nd'
    },
    {
      id: 4,
      name: 'James Thompson',
      position: 'Mayor',
      level: 'local',
      state: 'GA',
      party: 'Republican',
      photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      bio: 'Former police chief dedicated to law and order and transparent government.',
      priorities: ['Public Safety', 'Infrastructure', 'Transparent Government'],
      website: 'https://jamesthompson.com',
      twitter: '@JamesThompsonGA',
      facebook: 'ThompsonForMayor',
      endorsements: 15,
      fundraised: '$125K',
      nextEvent: 'Debate - March 25th'
    },
    {
      id: 5,
      name: 'David Wilson',
      position: 'U.S. House District 7',
      level: 'federal',
      state: 'OH',
      party: 'Republican',
      photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
      bio: 'Conservative attorney with a proven track record of defending constitutional principles.',
      priorities: ['Constitutional Rights', 'Healthcare Freedom', 'Energy Independence'],
      website: 'https://davidwilson.com',
      twitter: '@DavidWilsonOH',
      facebook: 'WilsonForCongress',
      endorsements: 38,
      fundraised: '$1.8M',
      nextEvent: 'Fundraiser - March 24th'
    },
    {
      id: 6,
      name: 'Jennifer Adams',
      position: 'School Board',
      level: 'local',
      state: 'TX',
      party: 'Republican',
      photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face',
      bio: 'Parent and education advocate fighting for parental rights and academic excellence.',
      priorities: ['Parental Rights', 'Academic Excellence', 'Fiscal Responsibility'],
      website: 'https://jenniferadams.com',
      twitter: '@JenniferAdamsTX',
      facebook: 'AdamsForSchoolBoard',
      endorsements: 22,
      fundraised: '$75K',
      nextEvent: 'Parent Meeting - March 21st'
    }
  ];

  const filteredCandidates = candidates.filter(candidate => {
    const matchesSearch = candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         candidate.position.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLevel = selectedLevel === 'all' || candidate.level === selectedLevel;
    const matchesState = selectedState === 'all' || candidate.state === selectedState;
    return matchesSearch && matchesLevel && matchesState;
  });

  const toggleSupport = (candidateId) => {
    const newSupported = new Set(supportedCandidates);
    if (newSupported.has(candidateId)) {
      newSupported.delete(candidateId);
    } else {
      newSupported.add(candidateId);
    }
    setSupportedCandidates(newSupported);
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'federal': return 'bg-patriot-red text-white';
      case 'state': return 'bg-patriot-navy text-white';
      case 'local': return 'bg-patriot-gold text-white';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-patriot-navy mb-4">Conservative Candidates</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover and support conservative candidates running for office at all levels of government. 
            Make your voice heard by backing principled leaders.
          </p>
        </motion.div>

        {/* Search and Filters */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search candidates by name or position..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-4">
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
              >
                {levels.map(level => (
                  <option key={level.id} value={level.id}>{level.name}</option>
                ))}
              </select>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
              >
                {states.map(state => (
                  <option key={state.id} value={state.id}>{state.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Candidates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCandidates.map((candidate, index) => (
            <motion.div
              key={candidate.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <img
                      src={candidate.photo}
                      alt={candidate.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="text-lg font-semibold text-patriot-navy">
                        {candidate.name}
                      </h3>
                      <p className="text-gray-600 text-sm">{candidate.position}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => toggleSupport(candidate.id)}
                      className={`p-2 rounded-full ${
                        supportedCandidates.has(candidate.id)
                          ? 'bg-patriot-red text-white'
                          : 'bg-gray-100 text-gray-600 hover:text-patriot-red'
                      } transition-colors duration-200`}
                    >
                      <SafeIcon icon={FiHeart} className="h-4 w-4" />
                    </button>
                    <button className="p-2 rounded-full bg-gray-100 text-gray-600 hover:text-patriot-red transition-colors duration-200">
                      <SafeIcon icon={FiShare2} className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getLevelColor(candidate.level)}`}>
                    {candidate.level}
                  </span>
                  <span className="ml-2 text-sm text-gray-600">{candidate.state}</span>
                </div>

                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {candidate.bio}
                </p>

                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Key Priorities:</h4>
                  <div className="flex flex-wrap gap-1">
                    {candidate.priorities.slice(0, 3).map((priority, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                      >
                        {priority}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div>
                    <span className="text-gray-600">Endorsements:</span>
                    <span className="font-medium ml-1">{candidate.endorsements}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Raised:</span>
                    <span className="font-medium ml-1">{candidate.fundraised}</span>
                  </div>
                </div>

                {candidate.nextEvent && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center text-sm text-gray-600">
                      <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
                      <span>Next: {candidate.nextEvent}</span>
                    </div>
                  </div>
                )}

                <div className="flex space-x-2">
                  <a
                    href={candidate.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-patriot-red text-white px-4 py-2 rounded-lg hover:bg-patriot-red-dark transition-colors duration-200 font-medium text-center text-sm flex items-center justify-center"
                  >
                    <SafeIcon icon={FiExternalLink} className="h-4 w-4 mr-1" />
                    Visit Website
                  </a>
                  <button className="px-4 py-2 border border-patriot-red text-patriot-red rounded-lg hover:bg-patriot-red hover:text-white transition-colors duration-200 font-medium text-sm">
                    Donate
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredCandidates.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiUser} className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No candidates found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Candidates;